import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Camera, Upload, FileText, Download, Printer, Loader2, Trash2,
  User, Mail, Phone, MapPin, Calendar, Briefcase, Shield, Heart, Save
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import SecureImage from './ui/SecureImage';
import { toast } from 'sonner';

const ID_DOC_TYPES = [
  { value: 'cni', label: "Carte Nationale d'Identite" },
  { value: 'passport', label: 'Passeport' },
  { value: 'permis', label: 'Permis de conduire' },
  { value: 'carte_sejour', label: 'Carte de sejour' },
  { value: 'autre', label: 'Autre' },
];

export const CollaboratorProfileModal = ({ open, userId, onClose, onUpdated }) => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({});
  const [activeTab, setActiveTab] = useState('info');

  const photoInputRef = useRef(null);
  const idInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);

  const [idDocForm, setIdDocForm] = useState({
    doc_type: 'cni',
    doc_number: '',
    issued_date: '',
    expiry_date: '',
  });

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const res = await api.get(`/users/${userId}/profile`);
      setUser(res.data);
      setForm({
        phone: res.data.phone || '',
        address: res.data.address || '',
        birth_date: res.data.birth_date || '',
        birth_place: res.data.birth_place || '',
        gender: res.data.gender || '',
        nationality: res.data.nationality || '',
        position: res.data.position || '',
        department: res.data.department || '',
        hire_date: res.data.hire_date || '',
        seniority_date: res.data.seniority_date || '',
        contract_start_date: res.data.contract_start_date || '',
        contract_end_date: res.data.contract_end_date || '',
        contract_type: res.data.contract_type || 'CDI',
        monthly_salary: res.data.monthly_salary || '',
        leave_balance: res.data.leave_balance ?? '',
        leave_accrual_rate: res.data.leave_accrual_rate ?? 2.5,
        emergency_contact_name: res.data.emergency_contact_name || '',
        emergency_contact_phone: res.data.emergency_contact_phone || '',
        blood_type: res.data.blood_type || '',
        notes: res.data.notes || '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de chargement');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => { if (open && userId) fetchUser(); }, [open, userId, fetchUser]);

  const idFullUrl = user?.id_document?.file_url
    ? (user.id_document.file_url.startsWith('http') ? user.id_document.file_url : `${process.env.REACT_APP_BACKEND_URL}${user.id_document.file_url}`)
    : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      // Clean payload: empty strings -> null for numeric/date fields so Pydantic doesn't 422
      const NUMERIC_FIELDS = ['monthly_salary', 'leave_balance', 'leave_accrual_rate', 'children_count'];
      const DATE_FIELDS = ['birth_date', 'hire_date', 'seniority_date', 'contract_start_date', 'contract_end_date'];
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' && (NUMERIC_FIELDS.includes(k) || DATE_FIELDS.includes(k))) {
          payload[k] = null;
        }
      });
      const res = await api.put(`/users/${userId}/profile`, payload);
      setUser(res.data);
      toast.success('Fiche enregistree');
      onUpdated?.();
    } catch (error) {
      // 422 returns detail as an array of objects; coerce to a readable string to avoid React crash
      let msg = 'Erreur lors de l\'enregistrement';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length) {
        msg = detail.map(d => `${(d.loc || []).slice(-1)[0] || 'champ'}: ${d.msg}`).join(' | ');
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/users/${userId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUser(u => ({ ...u, photo_url: res.data.photo_url }));
      toast.success('Photo mise a jour');
      onUpdated?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm('Supprimer la photo ?')) return;
    try {
      await api.delete(`/users/${userId}/photo`);
      setUser(u => ({ ...u, photo_url: null }));
      toast.success('Photo supprimee');
      onUpdated?.();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleUploadIdDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingId(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', idDocForm.doc_type);
      fd.append('doc_number', idDocForm.doc_number);
      fd.append('issued_date', idDocForm.issued_date);
      fd.append('expiry_date', idDocForm.expiry_date);
      const res = await api.post(`/users/${userId}/id-document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUser(u => ({ ...u, id_document: res.data }));
      setIdDocForm({ doc_type: 'cni', doc_number: '', issued_date: '', expiry_date: '' });
      toast.success("Piece d'identite enregistree");
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploadingId(false);
      e.target.value = '';
    }
  };

  const handleDeleteIdDoc = async () => {
    if (!window.confirm("Supprimer la piece d'identite ?")) return;
    try {
      await api.delete(`/users/${userId}/id-document`);
      setUser(u => ({ ...u, id_document: null }));
      toast.success('Document supprime');
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDownloadCard = async () => {
    try {
      const res = await api.get(`/users/${userId}/card/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carte_${(user?.name || 'collaborateur').replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Carte telechargee');
    } catch {
      toast.error('Erreur telechargement');
    }
  };

  const handleDownloadContract = async () => {
    try {
      const res = await api.get(`/users/${userId}/employment-contract/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contrat_travail_${(user?.name || 'collaborateur').replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Contrat de travail telecharge');
    } catch {
      toast.error('Erreur telechargement contrat');
    }
  };

  const handlePrintCard = async () => {
    try {
      const res = await api.get(`/users/${userId}/card/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url);
      if (win) {
        win.onload = () => {
          setTimeout(() => win.print(), 800);
        };
      }
    } catch {
      toast.error('Erreur impression');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[780px] max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1B3A4B]">
            <User className="w-5 h-5" /> Fiche Collaborateur
          </DialogTitle>
          <DialogDescription>
            Gestion complete du profil, piece d'identite et carte imprimable
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : user ? (
          <>
            <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#1B3A4B] to-[#2D5469] rounded-lg flex-shrink-0">
              <div className="relative group">
                <div className="w-20 h-20 rounded-full bg-white/10 border-2 border-[#D4A745] overflow-hidden flex items-center justify-center flex-shrink-0">
                  {user?.photo_url ? (
                    <SecureImage src={user.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-white/60" />
                  )}
                </div>
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white transition-opacity"
                  data-testid="upload-photo-btn"
                >
                  {uploadingPhoto ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUploadPhoto}
                />
              </div>
              <div className="flex-1 text-white min-w-0">
                <h3 className="text-xl font-bold truncate">{user.name}</h3>
                <p className="text-sm text-[#D4A745] font-mono">{user.employee_id}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className="bg-white/20 border-0 text-white">{user.role}</Badge>
                  {user.position && <span className="text-xs text-white/80">{user.position}</span>}
                </div>
              </div>
              {user?.photo_url && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeletePhoto}
                  className="text-white hover:text-red-300 hover:bg-white/10"
                  title="Supprimer photo"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col min-h-0">
              <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-4' : 'grid-cols-3'} flex-shrink-0`}>
                <TabsTrigger value="info" data-testid="tab-info">Informations</TabsTrigger>
                <TabsTrigger value="id" data-testid="tab-id">Piece d'identite</TabsTrigger>
                <TabsTrigger value="card" data-testid="tab-card">Carte</TabsTrigger>
                {isAdmin && <TabsTrigger value="contract" data-testid="tab-contract">Contrat</TabsTrigger>}
              </TabsList>

              {/* TAB: INFO */}
              <TabsContent value="info" className="flex-1 overflow-y-auto min-h-0 space-y-4 px-1 py-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Telephone" icon={Phone}>
                    <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+223 ..." data-testid="profile-phone" />
                  </Field>
                  <Field label="Adresse" icon={MapPin}>
                    <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Quartier, Ville" data-testid="profile-address" />
                  </Field>
                  <Field label="Date de naissance" icon={Calendar}>
                    <Input type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} data-testid="profile-birth-date" />
                  </Field>
                  <Field label="Lieu de naissance" icon={MapPin}>
                    <Input value={form.birth_place} onChange={e => setForm({ ...form, birth_place: e.target.value })} />
                  </Field>
                  <Field label="Sexe" icon={User}>
                    <Select value={form.gender || ''} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Masculin</SelectItem>
                        <SelectItem value="F">Feminin</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Nationalite" icon={Shield}>
                    <Input value={form.nationality} onChange={e => setForm({ ...form, nationality: e.target.value })} placeholder="Malienne" />
                  </Field>
                  <Field label="Fonction / Poste" icon={Briefcase}>
                    <Input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder="Agent commercial" data-testid="profile-position" />
                  </Field>
                  <Field label="Departement" icon={Briefcase}>
                    <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} />
                  </Field>
                  <Field label="Date d'embauche (entree)" icon={Calendar}>
                    <Input type="date" value={form.hire_date} onChange={e => setForm({ ...form, hire_date: e.target.value })} data-testid="profile-hire-date" />
                  </Field>
                  <Field label="Date d'anciennete" icon={Calendar}>
                    <Input
                      type="date"
                      value={form.seniority_date}
                      onChange={e => setForm({ ...form, seniority_date: e.target.value })}
                      placeholder="Laisser vide = date d'embauche"
                      data-testid="profile-seniority-date"
                    />
                  </Field>
                  <Field label="Groupe sanguin" icon={Heart}>
                    <Select value={form.blood_type || ''} onValueChange={(v) => setForm({ ...form, blood_type: v })}>
                      <SelectTrigger><SelectValue placeholder="Selectionner" /></SelectTrigger>
                      <SelectContent>
                        {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bt => (
                          <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Conditions du contrat</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Type de contrat" icon={Briefcase}>
                      <Select value={form.contract_type || 'CDI'} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                        <SelectTrigger data-testid="profile-contract-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CDI">CDI - Duree indeterminee</SelectItem>
                          <SelectItem value="CDD">CDD - Duree determinee</SelectItem>
                          <SelectItem value="STAGE">Stage</SelectItem>
                          <SelectItem value="CONSULTING">Consulting / Freelance</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Salaire mensuel brut (FCFA)" icon={Heart}>
                      <Input
                        type="number"
                        value={form.monthly_salary}
                        onChange={e => setForm({ ...form, monthly_salary: e.target.value ? parseFloat(e.target.value) : '' })}
                        placeholder="Ex: 250000"
                        data-testid="profile-monthly-salary"
                      />
                    </Field>
                    <Field label="Date de debut du contrat" icon={Calendar}>
                      <Input type="date" value={form.contract_start_date} onChange={e => setForm({ ...form, contract_start_date: e.target.value })} data-testid="profile-contract-start" />
                    </Field>
                    <Field label="Date de fin du contrat (CDD)" icon={Calendar}>
                      <Input type="date" value={form.contract_end_date} onChange={e => setForm({ ...form, contract_end_date: e.target.value })} placeholder="Laisser vide pour CDI" data-testid="profile-contract-end" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3 pt-3 border-t">
                    <Field label="Solde de conges actuel (jours)" icon={Calendar}>
                      <Input
                        type="number"
                        step="0.5"
                        value={form.leave_balance}
                        onChange={e => setForm({ ...form, leave_balance: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        placeholder="Ex: 15.5"
                        data-testid="profile-leave-balance"
                      />
                    </Field>
                    <Field label="Jours acquis / mois" icon={Calendar}>
                      <Input
                        type="number"
                        step="0.1"
                        value={form.leave_accrual_rate}
                        onChange={e => setForm({ ...form, leave_accrual_rate: e.target.value === '' ? 2.5 : parseFloat(e.target.value) })}
                        placeholder="2.5"
                        data-testid="profile-leave-accrual"
                      />
                    </Field>
                  </div>
                </div>

                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Contact d'urgence</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Nom">
                      <Input value={form.emergency_contact_name} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} />
                    </Field>
                    <Field label="Telephone">
                      <Input value={form.emergency_contact_phone} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} />
                    </Field>
                    <Field label="Lien de parente">
                      <Input value={form.emergency_contact_relation} onChange={e => setForm({ ...form, emergency_contact_relation: e.target.value })} placeholder="Conjoint, Parent..." />
                    </Field>
                  </div>
                </div>

                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Situation familiale & administrative</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Situation matrimoniale">
                      <Select value={form.marital_status || 'none'} onValueChange={(v) => setForm({ ...form, marital_status: v === 'none' ? '' : v })}>
                        <SelectTrigger data-testid="profile-marital"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non renseigne</SelectItem>
                          <SelectItem value="celibataire">Celibataire</SelectItem>
                          <SelectItem value="marie">Marie(e)</SelectItem>
                          <SelectItem value="divorce">Divorce(e)</SelectItem>
                          <SelectItem value="veuf">Veuf(ve)</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Nombre d'enfants">
                      <Input type="number" min="0" value={form.children_count ?? ''} onChange={e => setForm({ ...form, children_count: e.target.value === '' ? '' : parseInt(e.target.value, 10) })} placeholder="0" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <Field label="N INPS">
                      <Input value={form.inps_number} onChange={e => setForm({ ...form, inps_number: e.target.value })} placeholder="Numero INPS" data-testid="profile-inps" />
                    </Field>
                    <Field label="N AMO">
                      <Input value={form.amo_number} onChange={e => setForm({ ...form, amo_number: e.target.value })} placeholder="Numero AMO" data-testid="profile-amo" />
                    </Field>
                  </div>
                </div>

                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Formation & competences</p>
                  <Field label="Diplome principal / Niveau d'etudes">
                    <Input value={form.education_level} onChange={e => setForm({ ...form, education_level: e.target.value })} placeholder="Bac+3 Gestion, BTS Comptabilite..." data-testid="profile-education" />
                  </Field>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <Field label="Langues parlees">
                      <Input value={form.languages} onChange={e => setForm({ ...form, languages: e.target.value })} placeholder="Bambara, Francais, Anglais" />
                    </Field>
                    <Field label="Competences">
                      <Input value={form.skills} onChange={e => setForm({ ...form, skills: e.target.value })} placeholder="Negociation, Excel, Comptabilite" />
                    </Field>
                  </div>
                </div>

                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Coordonnees bancaires</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Banque">
                      <Input value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} placeholder="BDM, Orabank..." />
                    </Field>
                    <Field label="Numero de compte">
                      <Input value={form.bank_account} onChange={e => setForm({ ...form, bank_account: e.target.value })} placeholder="123456789012" />
                    </Field>
                  </div>
                </div>

                <Field label="Notes">
                  <Textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Informations complementaires..." />
                </Field>

                <div className="flex justify-end border-t pt-3">
                  <Button onClick={handleSave} disabled={saving} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="save-profile-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer la fiche
                  </Button>
                </div>
              </TabsContent>

              {/* TAB: ID DOC */}
              <TabsContent value="id" className="flex-1 overflow-y-auto min-h-0 space-y-4 px-1 py-2">
                {user.id_document ? (
                  <div className="space-y-3">
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#1B3A4B] text-[#D4A745] flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{user.id_document.doc_type_label}</p>
                            {user.id_document.doc_number && (
                              <p className="text-sm text-gray-600">N° {user.id_document.doc_number}</p>
                            )}
                            <div className="flex gap-3 text-xs text-gray-500 mt-1">
                              {user.id_document.issued_date && <span>Delivre : {user.id_document.issued_date}</span>}
                              {user.id_document.expiry_date && <span>Expire : {user.id_document.expiry_date}</span>}
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={handleDeleteIdDoc} className="text-red-600" data-testid="delete-id-btn">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {idFullUrl && (
                        <div className="mt-3 pt-3 border-t">
                          {user.id_document.file_ext === '.pdf' ? (
                            <a href={idFullUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#1B3A4B] underline text-sm">
                              <FileText className="w-4 h-4" /> Ouvrir le PDF
                            </a>
                          ) : (
                            <SecureImage src={user.id_document.file_url} alt="ID" className="max-h-64 rounded border" />
                          )}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Remplacer ce document ? Supprimez-le d'abord puis uploadez-en un nouveau.
                    </p>
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-5 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Type de document">
                        <Select value={idDocForm.doc_type} onValueChange={(v) => setIdDocForm(f => ({ ...f, doc_type: v }))}>
                          <SelectTrigger data-testid="id-doc-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ID_DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Numero de document">
                        <Input value={idDocForm.doc_number} onChange={e => setIdDocForm(f => ({ ...f, doc_number: e.target.value }))} data-testid="id-doc-number" />
                      </Field>
                      <Field label="Date de delivrance">
                        <Input type="date" value={idDocForm.issued_date} onChange={e => setIdDocForm(f => ({ ...f, issued_date: e.target.value }))} />
                      </Field>
                      <Field label="Date d'expiration">
                        <Input type="date" value={idDocForm.expiry_date} onChange={e => setIdDocForm(f => ({ ...f, expiry_date: e.target.value }))} />
                      </Field>
                    </div>
                    <div className="flex items-center gap-2 pt-2">
                      <input
                        ref={idInputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={handleUploadIdDoc}
                      />
                      <Button
                        onClick={() => idInputRef.current?.click()}
                        disabled={uploadingId}
                        className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                        data-testid="upload-id-btn"
                      >
                        {uploadingId ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Uploader le document (JPG/PNG/PDF)
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* TAB: CARD */}
              <TabsContent value="card" className="flex-1 overflow-y-auto min-h-0 space-y-4 px-1 py-2">
                <div className="bg-gradient-to-br from-[#1B3A4B] to-[#2D5469] rounded-2xl p-6 text-white shadow-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-24 h-32 rounded-lg bg-white/10 border-2 border-[#D4A745] overflow-hidden flex items-center justify-center flex-shrink-0">
                      {user?.photo_url ? (
                        <SecureImage src={user.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-12 h-12 text-white/40" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#D4A745] tracking-widest">CARTE COLLABORATEUR</p>
                      <h3 className="text-xl font-bold mt-1 truncate">{user.name}</h3>
                      <p className="text-sm text-white/70 mt-0.5">
                        {user.position || { agent: 'Agent', admin: 'Administrateur', super_admin: 'Super Administrateur' }[user.role]}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                        <div>
                          <p className="text-[#D4A745] uppercase tracking-wide">Matricule</p>
                          <p className="font-mono font-semibold">{user.employee_id}</p>
                        </div>
                        {user.hire_date && (
                          <div>
                            <p className="text-[#D4A745] uppercase tracking-wide">En poste depuis</p>
                            <p className="font-semibold">{user.hire_date}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 pt-3 border-t border-white/20 flex items-center justify-between text-xs text-white/60">
                    <span>GAB'S SERVICES HABITAT</span>
                    <span>BAMAKO, MALI</span>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <b>Apercu simplifie.</b> Le PDF genere contient le recto + verso au format standard ID (85.6 x 54 mm)
                  a decouper. Assurez-vous d'avoir renseigne les informations essentielles et upload la photo.
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleDownloadCard} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="download-card-btn">
                    <Download className="w-4 h-4 mr-2" /> Telecharger PDF
                  </Button>
                  <Button onClick={handlePrintCard} variant="outline" className="border-[#1B3A4B] text-[#1B3A4B]" data-testid="print-card-btn">
                    <Printer className="w-4 h-4 mr-2" /> Imprimer
                  </Button>
                </div>
              </TabsContent>

              {/* TAB: EMPLOYMENT CONTRACT - Admin only */}
              {isAdmin && (
              <TabsContent value="contract" className="flex-1 overflow-y-auto min-h-0 space-y-4 px-1 py-2">
                <div className="bg-gradient-to-br from-[#1B3A4B] to-[#2D5469] rounded-lg p-5 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-[#D4A745] flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-[#1B3A4B]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">Contrat de Travail</h3>
                      <p className="text-xs text-white/80">
                        Genere a partir du modele personnalisable de votre agence
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 space-y-1">
                  <p className="font-semibold">Les informations suivantes seront inserees dans le contrat :</p>
                  <ul className="list-disc pl-5 text-xs space-y-0.5">
                    <li>Nom, matricule, coordonnees du collaborateur</li>
                    <li>Fonction : <b>{user?.position || <span className="text-red-500">Non renseigne</span>}</b></li>
                    <li>Type de contrat : <b>{user?.contract_type || 'CDI'}</b></li>
                    <li>Date de debut : <b>{user?.contract_start_date || user?.hire_date || <span className="text-red-500">Non renseigne</span>}</b></li>
                    <li>Date de fin : <b>{user?.contract_end_date || <span className="text-gray-500 italic">Indeterminee (CDI)</span>}</b></li>
                    <li>Salaire mensuel brut : <b>{user?.monthly_salary ? `${Number(user.monthly_salary).toLocaleString('fr-FR').replace(/,/g,' ')} FCFA` : <span className="text-red-500">Non renseigne</span>}</b></li>
                  </ul>
                  {(!user?.position || !(user?.contract_start_date || user?.hire_date) || !user?.monthly_salary) && (
                    <p className="mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                      <b>Conseil :</b> Completez la <b>fonction</b>, la <b>date de debut du contrat</b> et le <b>salaire mensuel</b> dans l'onglet
                      "Informations" avant de generer le contrat.
                    </p>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <b>Modele</b> : Le contenu provient du modele <b>Contrat de Travail</b> configurable dans
                  <a href="/document-templates" className="text-[#1B3A4B] underline ml-1">Modeles Documents</a>.
                  Toute modification du modele s'appliquera aux prochains contrats generes.
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleDownloadContract} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="download-contract-btn">
                    <Download className="w-4 h-4 mr-2" /> Generer le contrat PDF
                  </Button>
                  <a href="/document-templates" target="_blank" rel="noreferrer">
                    <Button variant="outline" className="border-[#1B3A4B] text-[#1B3A4B]">
                      <FileText className="w-4 h-4 mr-2" /> Editer le modele
                    </Button>
                  </a>
                </div>
              </TabsContent>
              )}
            </Tabs>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, icon: Icon, children }) => (
  <div>
    <Label className="text-xs text-gray-600 flex items-center gap-1 mb-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </Label>
    {children}
  </div>
);
